import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import type { LoadResult } from "@picnic-field/local-host/protocol";
import type { PaletteLike } from "@picnic-field/renderer/appearance";
import { fitGalleryView } from "@picnic-field/renderer/galleryMap";
import { readLod } from "@picnic-field/renderer/lod";
import { useDensity } from "../../../store/density";
import { useI18n } from "../../../i18n";
import { workTitle } from "../../upload";
import { courseDeps } from "../../course";
import type { GalleryViewLike } from "../../course";
import {
  applyViewPrinciple,
  PrincipleButton,
  PrincipleLabelLayer,
  PrincipleLegend,
  usePrincipleLabels,
} from "../../course";
import type { PrincipleState } from "../../course";
import type { HostHandle } from "../../course";
import type { GalleryItem } from "../../../lib/api/socialApi";
import {
  boundsOf,
  clampTo,
  dragTo,
  keyDir,
  nearest,
  planX,
  planY,
  standBefore,
  walk,
} from "../lib/logic";
import type { GalleryViewState, ActivityMap, Bounds, Pedestal, Point } from "../types";
import { ZoomControls } from "../../../components/ZoomControls";

export function pedestalsFrom(load: LoadResult, tf: Float32Array | null): Pedestal[] {
  if (!tf) return [];
  return load.bodies
    .filter((b) => b.kind === "env" && b.surface === "pedestal" && b.owner !== "env")
    .map((b) => ({
      owner: b.owner,
      index: b.index,
      x: tf[b.index * 7] ?? 0,
      z: tf[b.index * 7 + 2] ?? 0,
    }));
}

/**
 * FS-03-01 전시관 공간 — 3D(받침대 ≤30 · C-02 createGalleryView) + 작품 라벨(별명 · 막힘 빨간 포트) + 미니맵 + HUD.
 * 이동: 끌기(터치·마우스) · 방향키/WASD. 받침대에 다가가면 작품 카드(onApproach). 3D 가 안 되면 위에서 본 배치도로 바꾼다.
 * 원리 보기(P2-S5 · SR-PHY-07): 오른쪽 위 토글 — 작동 중인 작품의 화살표 오버레이(SE 화살표만 · SU 수치). 이모트 바(PH2)는 흐리게 자리만.
 * data-view: loading | 3d | plan — E2E 가 첫 장면을 기다리는 데 쓴다.
 */
export function GalleryScene({
  world,
  items,
  palette,
  active,
  selected,
  pos,
  onMove,
  onApproach,
  onPick,
  children,
  onView,
  principle,
  onPrinciple,
}: {
  world: HostHandle;
  items: readonly GalleryItem[];
  palette: PaletteLike | null;
  active: ActivityMap;
  selected: string | null;
  pos: Point | null;
  onMove: (p: Point) => void;
  onApproach: (owner: string | null) => void;
  onPick: (owner: string) => void;
  children?: ReactNode;
  onView?: (v: GalleryViewLike | null) => void;
  /** P2-S5 원리 보기 상태 · 토글(없으면 버튼 없음) */
  principle?: PrincipleState;
  onPrinciple?: () => void;
}) {
  const { t, locale } = useI18n();
  const { density } = useDensity();
  const se = density === "se";
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const sceneRef = useRef<HTMLDivElement>(null);
  const labelRefs = useRef(new Map<string, HTMLButtonElement>());
  const viewRef = useRef<GalleryViewLike | null>(null);
  const [view, setView] = useState<GalleryViewState>("loading");
  const [pedestals, setPedestals] = useState<Pedestal[]>([]);
  const [mapOpen, setMapOpen] = useState(false);
  const load = world.load;
  const onViewRef = useRef(onView);
  onViewRef.current = onView;
  const principleRef = useRef(principle);
  principleRef.current = principle;
  const labels = usePrincipleLabels();
  const byId = useMemo(() => new Map(items.map((i) => [i.snapshotId, i])), [items]);

  // 받침대 자리 — 첫 프레임에서(정적)
  useEffect(() => {
    if (!load) return;
    const f = world.latestFrame();
    if (f) setPedestals(pedestalsFrom(load, f.transforms));
    return world.onFrame((fr) => {
      setPedestals((ps) => (ps.length ? ps : pedestalsFrom(load, fr.transforms)));
    });
  }, [load, world]);

  const bounds: Bounds = useMemo(() => boundsOf(pedestals), [pedestals]);
  const posRef = useRef<Point | null>(pos);
  posRef.current = pos;

  // 처음 선 자리(S5 — S4 실측 보완): 앞줄 받침대를 채운 순서대로 화면 폭에 다 들어오는 만큼 골라 그 가운데 앞에 서고,
  // 카메라를 그만큼 물린다(작품이 적을 때 빈 곳을 보거나 화면 가장자리에서 잘리지 않게). 학생이 움직이면 기본 거리로 돌아간다.
  const fitBack = useRef<number | null>(null);
  useEffect(() => {
    if (!pedestals.length || posRef.current) return;
    const el = sceneRef.current;
    const aspect = el && el.clientHeight > 0 ? el.clientWidth / el.clientHeight : 0.8;
    const fit = fitGalleryView(pedestals, aspect);
    if (!fit) {
      onMove(clampTo(bounds, { x: 0, z: bounds.minZ + 0.2 }));
      return;
    }
    fitBack.current = fit.back;
    onMove(clampTo(bounds, { x: fit.x, z: fit.z }));
  }, [pedestals, bounds, onMove]);
  /** 학생이 움직였다(걷기 · 끌기 · 받침대 고르기) — 첫 화면 맞춤을 풀고 기본 거리로 */
  const userMove = useCallback(
    (p: Point) => {
      fitBack.current = null;
      onMove(p);
    },
    [onMove],
  );
  const viewOpts = () => (fitBack.current != null ? { back: fitBack.current } : undefined);

  // 받침대 접근 → 카드
  const nearOwner = useMemo(
    () => (pos ? (nearest(pedestals, pos)?.owner ?? null) : null),
    [pos, pedestals],
  );
  const onApproachRef = useRef(onApproach);
  onApproachRef.current = onApproach;
  useEffect(() => onApproachRef.current(nearOwner), [nearOwner]);

  // 3D 뷰
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!load || !canvas) return;
    let alive = true;
    let handle: GalleryViewLike | null = null;
    const offs: (() => void)[] = [];
    setView("loading");
    courseDeps()
      .createGalleryView(canvas, {
        bodies: load.bodies,
        exhibits: items.map((i) => ({
          owner: i.snapshotId,
          glbUrl: i.glbUrl,
          // S5 LOD(전시 항목의 선택 필드) — 정지 작품은 L2
          lod: readLod(i),
          appearance: i.appearance,
          palette,
        })),
        quality: lowEnd() ? "low" : "high",
        background: cssVar("--pf-color-scene", "#CFE3D5"),
        // SR-PHY-02 — 로컬 커널 프레임(틱마다)의 마지막 두 상태를 보간해 그린다
        interpolate: true,
      })
      .then(async (h) => {
        handle = h;
        if (!alive) return h.dispose();
        await h.ready;
        if (!alive) return;
        viewRef.current = h;
        const f = world.latestFrame();
        if (f) h.update(f);
        if (posRef.current) h.setViewpoint(posRef.current, viewOpts());
        offs.push(world.onFrame((fr) => h.update(fr)));
        const pedIndex = new Map(
          load.bodies.filter((b) => b.surface === "pedestal").map((b) => [b.owner, b.index]),
        );
        offs.push(
          h.onPaint((ts) => {
            world.probe.markPainted(ts);
            placeLabels(h, labelRefs.current, pedIndex);
            if (principleRef.current?.on) labels.place(h);
          }),
        );
        applyViewPrinciple(h, principleRef.current);
        setView("3d");
        onViewRef.current?.(h);
      })
      .catch(() => alive && setView("plan"));
    return () => {
      alive = false;
      offs.forEach((o) => o());
      handle?.dispose();
      viewRef.current = null;
      onViewRef.current?.(null);
    };
    // 작품 목록·팔레트는 load 와 함께 바뀐다
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [load]);

  useEffect(() => {
    if (pos) viewRef.current?.setViewpoint(pos, viewOpts());
  }, [pos]);

  // 원리 보기 토글 · 밀도(SU 수치)
  const pOn = principle?.on ?? false;
  const pNumbers = principle?.numbers ?? false;
  useEffect(() => {
    applyViewPrinciple(viewRef.current, { on: pOn, numbers: pNumbers });
    if (!pOn) labels.place(null);
    // labels 는 ref 묶음
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pOn, pNumbers, view]);

  const activeOwners = useMemo(() => Object.keys(active).sort().join("|"), [active]);
  useEffect(() => {
    viewRef.current?.setActive(activeOwners ? activeOwners.split("|") : []);
  }, [activeOwners, view]);

  // 방향키/WASD — 장면이나 문서 본문에 포커스가 있을 때만(카드·창의 버튼 조작을 가로채지 않게)
  useEffect(() => {
    const keys = new Set<string>();
    let raf = 0;
    let last = 0;
    const MOVE = [
      "ArrowUp",
      "ArrowDown",
      "ArrowLeft",
      "ArrowRight",
      "KeyW",
      "KeyA",
      "KeyS",
      "KeyD",
    ];
    const ours = () => {
      const a = document.activeElement;
      return (
        !a || a === document.body || (sceneRef.current?.contains(a) && !a.closest(".pf-card3"))
      );
    };
    const loop = (ts: number) => {
      const dt = last ? Math.min(0.1, (ts - last) / 1000) : 1 / 60;
      last = ts;
      const d = keyDir(keys);
      const p = posRef.current;
      if (p && (d.x || d.z)) userMove(walk(bounds, p, d, dt));
      raf = keys.size ? requestAnimationFrame(loop) : 0;
      if (!raf) last = 0;
    };
    const down = (e: KeyboardEvent) => {
      if (!MOVE.includes(e.code) || !ours()) return;
      e.preventDefault();
      keys.add(e.code);
      if (!raf) raf = requestAnimationFrame(loop);
    };
    const up = (e: KeyboardEvent) => keys.delete(e.code);
    const blur = () => keys.clear();
    window.addEventListener("keydown", down);
    window.addEventListener("keyup", up);
    window.addEventListener("blur", blur);
    return () => {
      window.removeEventListener("keydown", down);
      window.removeEventListener("keyup", up);
      window.removeEventListener("blur", blur);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [bounds, userMove]);

  // 끌어서 돌아다니기(포인터) — 라벨·카드·HUD 위에서는 시작하지 않는다
  const drag = useRef<{ id: number; x: number; y: number; start: Point } | null>(null);
  const pxPerM = () => Math.max(80, (sceneRef.current?.clientHeight ?? 400) / 2.5);

  /** 라벨·미니맵에서 고르면 그 받침대 앞으로 걸어간다(그러면 접근 규칙으로 카드가 열린다) */
  const goTo = (owner: string) => {
    const p = pedestals.find((x) => x.owner === owner);
    if (p) userMove(clampTo(bounds, standBefore(p)));
    onPick(owner);
  };
  const occupied = new Set(pedestals.map((p) => p.owner));
  const labelled = items.filter((i) => occupied.has(i.snapshotId));

  return (
    <div
      ref={sceneRef}
      className="pf-gallery__scene"
      data-view={view}
      data-testid="gallery-scene"
      data-pos={pos ? `${pos.x.toFixed(2)},${pos.z.toFixed(2)}` : ""}
      data-principle={pOn ? (pNumbers ? "su" : "se") : "off"}
      role="application"
      aria-roledescription={t("gallery.scene.role")}
      aria-label={t("gallery.scene.label")}
      tabIndex={0}
      onPointerDown={(e) => {
        const target = e.target as HTMLElement;
        if (target.closest("button, a, [role=dialog]") || !posRef.current) return;
        drag.current = { id: e.pointerId, x: e.clientX, y: e.clientY, start: posRef.current };
        (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
      }}
      onPointerMove={(e) => {
        const d = drag.current;
        if (!d || d.id !== e.pointerId) return;
        userMove(dragTo(bounds, d.start, e.clientX - d.x, e.clientY - d.y, pxPerM()));
      }}
      onPointerUp={() => (drag.current = null)}
      onPointerCancel={() => (drag.current = null)}
    >
      <canvas ref={canvasRef} className="pf-gallery__canvas" hidden={view === "plan"} />
      {view !== "plan" && <ZoomControls getView={() => viewRef.current} wheelTarget={canvasRef} />}
      {view === "plan" && load && (
        <PlanView pedestals={pedestals} bounds={bounds} pos={pos} labelRefs={labelRefs} />
      )}
      {labelled.map((i) => {
        const a = active[i.snapshotId];
        const port = a?.stalled[0] ?? null;
        return (
          <button
            key={i.snapshotId}
            type="button"
            ref={(el) => {
              if (el) labelRefs.current.set(i.snapshotId, el);
              else labelRefs.current.delete(i.snapshotId);
            }}
            className={[
              "pf-gallery__label",
              i.mine && "pf-gallery__label--me",
              port && "pf-gallery__label--stall",
              a && "pf-gallery__label--on",
              selected === i.snapshotId && "pf-gallery__label--sel",
            ]
              .filter(Boolean)
              .join(" ")}
            data-testid="exhibit-label"
            data-owner={i.snapshotId}
            data-stalled={port ?? ""}
            aria-label={t("gallery.label.aria", {
              name: i.mine ? t("gallery.label.mine") : i.nickname,
              // 서버 제목이 모형 slug 면 모형 이름으로(스크린리더가 'base-vehicle' 을 읽지 않게) — 별명은 name 에 있다
              title: workTitle(t, locale, i, ""),
            })}
            onClick={() => goTo(i.snapshotId)}
          >
            {i.mine ? t("gallery.label.mine") : i.nickname}
            {port && (
              <span className="pf-gallery__port" aria-hidden="true">
                {" "}
                ⛔{port}
              </span>
            )}
          </button>
        );
      })}

      <div className="pf-gallery__hud pf-gallery__hud--tl">
        <button
          type="button"
          className="pf-hud pf-gallery__hudbtn"
          aria-expanded={mapOpen}
          aria-controls="gallery-minimap"
          onClick={() => setMapOpen((o) => !o)}
          data-testid="minimap-toggle"
        >
          ▦ {t("gallery.hud.minimap")}
        </button>
        {mapOpen && (
          <Minimap
            pedestals={pedestals}
            bounds={bounds}
            pos={pos}
            items={byId}
            active={active}
            onPick={goTo}
          />
        )}
      </div>
      <PrincipleLabelLayer layer={labels.layer} on={pOn && pNumbers && view === "3d"} />
      {onPrinciple && (
        <div className="pf-gallery__hud pf-gallery__hud--tr">
          <PrincipleButton hud on={pOn} onToggle={onPrinciple} />
          {pOn && <PrincipleLegend se={se} />}
        </div>
      )}
      <div className="pf-gallery__hud pf-gallery__hud--bl">
        <span className="pf-hud" data-testid="hud-walk">
          {se ? `👆 ${t("gallery.hud.walkSE")}` : t("gallery.hud.walkSU")}
        </span>
      </div>
      <div className="pf-gallery__hud pf-gallery__hud--br">
        <span className="pf-hud pf-hud--ph2" aria-disabled="true" data-testid="hud-emotes">
          😊 👏 😮 ❤ {t("gallery.hud.ph2")}
        </span>
      </div>
      {children}
    </div>
  );
}

function Minimap({
  pedestals,
  bounds,
  pos,
  items,
  active,
  onPick,
}: {
  pedestals: readonly Pedestal[];
  bounds: Bounds;
  pos: Point | null;
  items: ReadonlyMap<string, GalleryItem>;
  active: ActivityMap;
  onPick: (owner: string) => void;
}) {
  const { t } = useI18n();
  const w = bounds.maxX - bounds.minX;
  const h = bounds.maxZ - bounds.minZ;
  // 화면과 같은 방향(위 = 북쪽 +z · 오른쪽 = −x)
  const X = (x: number) => planX(bounds, x) * 160;
  const Y = (z: number) => planY(bounds, z) * ((160 * h) / w);
  return (
    <div className="pf-pane pf-gallery__map" id="gallery-minimap" data-testid="minimap">
      <svg
        viewBox={`0 0 160 ${(160 * h) / w}`}
        role="img"
        aria-label={t("gallery.hud.minimapAria")}
      >
        <rect
          className="pf-gallery__map-floor"
          x="0"
          y="0"
          width="160"
          height={(160 * h) / w}
          rx="6"
        />
        {pedestals.map((p) => {
          const i = items.get(p.owner);
          const a = active[p.owner];
          const cls = a?.stalled.length ? "stall" : a ? "on" : i?.mine ? "me" : "off";
          return (
            <rect
              key={p.owner}
              className={`pf-gallery__map-ped pf-gallery__map-ped--${cls}`}
              x={X(p.x) - 5}
              y={Y(p.z) - 5}
              width="10"
              height="10"
              rx="2"
              onClick={() => onPick(p.owner)}
            >
              <title>{i?.nickname ?? ""}</title>
            </rect>
          );
        })}
        {pos && <circle className="pf-gallery__map-me" cx={X(pos.x)} cy={Y(pos.z)} r="5" />}
      </svg>
    </div>
  );
}

/** 3D 대체 — 위에서 본 배치도(라벨 자리도 여기서) */
function PlanView({
  pedestals,
  bounds,
  pos,
  labelRefs,
}: {
  pedestals: readonly Pedestal[];
  bounds: Bounds;
  pos: Point | null;
  labelRefs: React.MutableRefObject<Map<string, HTMLButtonElement>>;
}) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const r = ref.current?.getBoundingClientRect();
    const W = r?.width || 600;
    const H = r?.height || 400;
    for (const p of pedestals) {
      const el = labelRefs.current.get(p.owner);
      if (!el) continue;
      const x = planX(bounds, p.x) * W;
      const y = planY(bounds, p.z) * H;
      el.style.visibility = "visible";
      el.style.transform = `translate(${Math.round(x)}px, ${Math.round(y)}px) translate(-50%, -100%)`;
    }
  });
  return (
    <div ref={ref} className="pf-gallery__plan" aria-hidden="true" data-testid="gallery-plan">
      {pos && (
        <span
          className="pf-gallery__plan-me"
          style={{
            left: `${planX(bounds, pos.x) * 100}%`,
            top: `${planY(bounds, pos.z) * 100}%`,
          }}
        />
      )}
    </div>
  );
}

function placeLabels(
  h: GalleryViewLike,
  refs: Map<string, HTMLButtonElement>,
  pedIndex: ReadonlyMap<string, number>,
) {
  for (const [owner, el] of refs) {
    const i = pedIndex.get(owner);
    const p = i != null ? h.project?.(i, 0.55) : null;
    if (!p) {
      el.style.visibility = "hidden";
      continue;
    }
    el.style.visibility = "visible";
    el.style.transform = `translate(${Math.round(p.x)}px, ${Math.round(p.y)}px) translate(-50%, -100%)`;
  }
}

function lowEnd(): boolean {
  const n = navigator as Navigator & { deviceMemory?: number };
  return (n.hardwareConcurrency ?? 8) <= 4 || (n.deviceMemory ?? 8) <= 4;
}

function cssVar(name: string, fallback: string): string {
  try {
    const v = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
    if (/^#[0-9a-f]{3,8}$/i.test(v)) return v;
  } catch {
    /* 없음 */
  }
  return fallback;
}
